/**
 * 양식 매칭 규칙 CRUD (환경설정)
 * SSOT: D:\295\erp\CM\design\05_양식레지스트리_매칭.md §5.2 · P2
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import useTenantCode from 'hooks/useTenantCode';
import { Card } from 'styles/common.styles';
import { SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, ActionButtonGroup } from 'components/forms';
import { showAlert } from 'utils/toastBridge';
import { DOC_TYPE, DOC_TYPE_LABEL, DOC_TYPE_OPTIONS } from 'system/formDocTypes';
import {
  hydrateFormRegistry,
  ensureFormRegistry,
  listForms,
  listMatchRules,
  upsertMatchRule,
  deleteMatchRule,
  resolveForm,
} from 'system/formRegistryStore';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  padding: 10px 12px;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  th,
  td {
    border: 1px solid var(--erp-border);
    padding: 4px 8px;
    text-align: left;
  }
  th {
    background: var(--erp-surface-muted);
    font-weight: 600;
  }
  tr.active td {
    background: var(--erp-color-bg-selected, var(--erp-surface-muted));
  }
  tr.disabled td {
    color: var(--erp-text-secondary);
    opacity: 0.75;
  }
`;

const Meta = styled.p`
  margin: 0;
  font-size: 11px;
  color: var(--erp-text-secondary);
`;

const emptyDraft = (tenantCode, docType) => ({
  ruleId: '',
  tenantCode: tenantCode || '*',
  docType: docType || DOC_TYPE.EXP_RESOLUTION,
  formId: '',
  deptCode: '',
  projectCode: '',
  fundCode: '',
  fiscalYear: '',
  priority: 10,
  useYn: 'Y',
});

export default function FormMatchRulesPanel() {
  const tenantCode = useTenantCode();
  const [rules, setRules] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [docTypeFilter, setDocTypeFilter] = useState(DOC_TYPE.EXP_RESOLUTION);

  const methods = useForm({
    defaultValues: {
      ...emptyDraft(tenantCode, DOC_TYPE.EXP_RESOLUTION),
      filterDocType: DOC_TYPE.EXP_RESOLUTION,
      previewDept: '',
      previewProject: '',
      previewFund: '',
      previewFy: '',
    },
  });
  const { reset, getValues, setValue } = methods;

  const refresh = useCallback(async () => {
    await hydrateFormRegistry();
    const dt = getValues('filterDocType') || docTypeFilter;
    setDocTypeFilter(dt);
    const list = listMatchRules({
      docType: dt,
      tenantCode,
      includeDisabled: true,
    });
    setRules(list);
    if (selectedId && !list.some((r) => r.ruleId === selectedId)) {
      setSelectedId('');
    }
  }, [getValues, tenantCode, docTypeFilter, selectedId]);

  useEffect(() => {
    refresh();
  }, [tenantCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const formOptions = useMemo(() => {
    const dt = getValues('docType') || docTypeFilter;
    return listForms({ docType: dt, useYn: 'Y' }).map((f) => ({
      value: f.formId,
      label: `${f.formName || f.formId} (${f.formId})`,
    }));
  }, [docTypeFilter, rules, getValues]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectRule = useCallback(
    (rule) => {
      if (!rule) return;
      setSelectedId(rule.ruleId);
      reset({
        ...getValues(),
        ruleId: rule.ruleId,
        tenantCode: rule.tenantCode || tenantCode,
        docType: rule.docType,
        formId: rule.formId,
        deptCode: rule.deptCode || '',
        projectCode: rule.projectCode || '',
        fundCode: rule.fundCode || '',
        fiscalYear: rule.fiscalYear || '',
        priority: rule.priority ?? 10,
        useYn: rule.useYn || 'Y',
      });
    },
    [getValues, reset, tenantCode],
  );

  const handleSearch = useCallback(() => {
    refresh();
    showAlert(`매칭 규칙 ${listMatchRules({ docType: getValues('filterDocType'), tenantCode, includeDisabled: true }).length}건`, 'info');
  }, [refresh, getValues, tenantCode]);

  const handleAdd = useCallback(() => {
    const dt = getValues('filterDocType') || DOC_TYPE.EXP_RESOLUTION;
    const forms = listForms({ docType: dt, useYn: 'Y' });
    const draft = emptyDraft(tenantCode, dt);
    draft.formId = forms[0]?.formId || '';
    setSelectedId('');
    reset({ ...getValues(), ...draft });
    showAlert('신규 규칙 입력 후 저장하세요. 빈 차원은 와일드카드(*)입니다.', 'info');
  }, [getValues, reset, tenantCode]);

  const handleSave = useCallback(() => {
    const v = getValues();
    try {
      const saved = upsertMatchRule({
        ruleId: v.ruleId || undefined,
        tenantCode: v.tenantCode || tenantCode,
        docType: v.docType,
        formId: v.formId,
        deptCode: v.deptCode,
        projectCode: v.projectCode,
        fundCode: v.fundCode,
        fiscalYear: v.fiscalYear,
        priority: v.priority,
        useYn: v.useYn,
      });
      setSelectedId(saved.ruleId);
      setValue('ruleId', saved.ruleId);
      refresh();
      showAlert(`규칙 저장: ${saved.ruleId}`, 'success');
    } catch (e) {
      showAlert(e?.message || '저장 실패', 'error');
    }
  }, [getValues, tenantCode, setValue, refresh]);

  const handleDelete = useCallback(() => {
    const id = getValues('ruleId') || selectedId;
    if (!id) {
      showAlert('삭제할 규칙을 선택하세요.', 'warning');
      return;
    }
    deleteMatchRule(id);
    setSelectedId('');
    reset({ ...getValues(), ...emptyDraft(tenantCode, getValues('filterDocType')) });
    refresh();
    showAlert(`규칙 비활성: ${id}`, 'success');
  }, [getValues, selectedId, reset, tenantCode, refresh]);

  const handlePreviewResolve = useCallback(() => {
    const v = getValues();
    const form = resolveForm(v.filterDocType || docTypeFilter, {
      tenantCode,
      deptCode: v.previewDept,
      projectCode: v.previewProject,
      fundCode: v.previewFund,
      fiscalYear: v.previewFy,
    });
    showAlert(
      form
        ? `미리보기 resolve → ${form.formId} (${form.formName || ''})`
        : '미리보기: 매칭·ACL 결과 양식 없음',
      form ? 'success' : 'warning',
    );
  }, [getValues, docTypeFilter, tenantCode]);

  const filterFields = useMemo(
    () => [
      {
        name: 'filterDocType',
        label: '업무',
        type: 'select',
        options: DOC_TYPE_OPTIONS,
        labelWidth: SEARCH_LABEL_W,
      },
    ],
    [],
  );

  const editFields = useMemo(
    () => [
      { name: 'ruleId', label: '규칙ID', type: 'text', readonly: true, labelWidth: SEARCH_LABEL_W },
      {
        name: 'tenantCode',
        label: '기관',
        type: 'text',
        labelWidth: SEARCH_LABEL_W,
        placeholder: '* = 전체',
      },
      {
        name: 'docType',
        label: '업무',
        type: 'select',
        options: DOC_TYPE_OPTIONS,
        labelWidth: SEARCH_LABEL_W,
      },
      {
        name: 'formId',
        label: '양식',
        type: 'select',
        options: formOptions.length
          ? formOptions
          : [{ value: '', label: '(양식 없음)' }],
        labelWidth: SEARCH_LABEL_W,
      },
      { name: 'deptCode', label: '부서', type: 'text', labelWidth: SEARCH_LABEL_W, placeholder: '빈값=*' },
      { name: 'projectCode', label: '사업', type: 'text', labelWidth: SEARCH_LABEL_W, placeholder: '빈값=*' },
      { name: 'fundCode', label: '재원', type: 'text', labelWidth: SEARCH_LABEL_W, placeholder: '빈값=*' },
      { name: 'fiscalYear', label: '회계연도', type: 'text', labelWidth: SEARCH_LABEL_W, placeholder: '빈값=*' },
      { name: 'priority', label: '우선순위', type: 'number', labelWidth: SEARCH_LABEL_W },
      {
        name: 'useYn',
        label: '사용',
        type: 'select',
        options: [
          { value: 'Y', label: 'Y' },
          { value: 'N', label: 'N' },
        ],
        labelWidth: SEARCH_LABEL_W,
      },
    ],
    [formOptions],
  );

  const previewFields = useMemo(
    () => [
      { name: 'previewDept', label: '부서', type: 'text', labelWidth: SEARCH_LABEL_W },
      { name: 'previewProject', label: '사업', type: 'text', labelWidth: SEARCH_LABEL_W },
      { name: 'previewFund', label: '재원', type: 'text', labelWidth: SEARCH_LABEL_W },
      { name: 'previewFy', label: '회계연도', type: 'text', labelWidth: SEARCH_LABEL_W },
    ],
    [],
  );

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', onClick: handleSearch, shortcut: 'F7' },
      { id: 'add', label: '추가', onClick: handleAdd, shortcut: 'F8' },
      { id: 'delete', label: '삭제', onClick: handleDelete, shortcut: 'F9', variant: 'danger' },
      { id: 'save', label: '저장', onClick: handleSave, shortcut: 'F10', variant: 'primary' },
      { id: 'preview', label: 'resolve 미리보기', onClick: handlePreviewResolve },
    ],
    [handleSearch, handleAdd, handleDelete, handleSave, handlePreviewResolve],
  );

  return (
    <Stack>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} />
        <Panel>
          <strong style={{ fontSize: 13 }}>조회</strong>
          <FormArea
            dense
            fields={filterFields}
            onFormChange={(formData, _prev, changed) => {
              if (changed === 'filterDocType' && formData?.filterDocType) {
                setDocTypeFilter(formData.filterDocType);
                setTimeout(refresh, 0);
              }
            }}
          />
          <Meta>
            longest-match(부서·사업·재원·연도) → priority. 기관 {tenantCode} · 업무{' '}
            {DOC_TYPE_LABEL[docTypeFilter] || docTypeFilter}
          </Meta>
        </Panel>
        <Panel>
          <strong style={{ fontSize: 13 }}>규칙 목록</strong>
          <Table>
            <thead>
              <tr>
                <th>ruleId</th>
                <th>formId</th>
                <th>기관</th>
                <th>부서</th>
                <th>사업</th>
                <th>재원</th>
                <th>연도</th>
                <th>우선</th>
                <th>사용</th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 ? (
                <tr>
                  <td colSpan={9}>규칙 없음 · 추가로 등록</td>
                </tr>
              ) : (
                rules.map((r) => (
                  <tr
                    key={r.ruleId}
                    className={`${selectedId === r.ruleId ? 'active' : ''} ${r.useYn === 'N' ? 'disabled' : ''}`}
                    onClick={() => selectRule(r)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{r.ruleId}</td>
                    <td>{r.formId}</td>
                    <td>{r.tenantCode || '*'}</td>
                    <td>{r.deptCode || '*'}</td>
                    <td>{r.projectCode || '*'}</td>
                    <td>{r.fundCode || '*'}</td>
                    <td>{r.fiscalYear || '*'}</td>
                    <td>{r.priority || 0}</td>
                    <td>{r.useYn || 'Y'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Panel>
        <Panel>
          <strong style={{ fontSize: 13 }}>규칙 편집</strong>
          <FormArea dense fields={editFields} />
        </Panel>
        <Panel>
          <strong style={{ fontSize: 13 }}>resolve 미리보기 컨텍스트</strong>
          <FormArea dense fields={previewFields} />
          <Meta>저장 없이 현재 규칙·ACL로 resolveForm 결과를 확인합니다.</Meta>
        </Panel>
      </FormProvider>
    </Stack>
  );
}
